import React, { useEffect } from 'react';

import { routes } from '@aline/routes';
import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { isAfter, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { ConfirmationDialog, ConfirmationDialogVariant } from 'src/components';
import { isCI, isDev } from 'src/config';
import { useCurrentUserQuery, useCurrentUserSubscription, useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { doNothing, redirectTo, useDispatchNotification } from 'src/helpers';

const MAX_WORKFLOWS_PER_MONTH = 1;

export function TierGuardModalProvider({
  children,
}: React.HTMLAttributes<HTMLElement>): JSX.Element {
  const tierGuardState = useTierGuardModalState();
  const roleGuardState = useRoleGuardModalState();

  return (
    <>
      {children}
      <TierGuardModal {...tierGuardState} />
      <RoleGuardModal {...roleGuardState} />
    </>
  );
}

/**
 * Appears when user with insufficient tier attempts an action.
 */
function TierGuardModal({
  open,
  pageBlock = false,
  reachedLimit = false,
  requiredTier = Apollo.SubscriptionTier.Team,
}: TierGuardModalState): JSX.Element {
  const navigate = useNavigate();
  const tier = displayTier(requiredTier);
  const currentUser = useSelectCurrentUser();
  const isAuthorizedToManageBilling = currentUser?.role === Apollo.OrgRole.Admin;
  const [createBillingSessionUrl] = Apollo.useCreateBillingSessionUrlMutation();
  const copy = reachedLimit
    ? `You have reached the limit of ${MAX_WORKFLOWS_PER_MONTH} workflows for the month on Aline's free plan. Upgrade now to create more workflows.`
    : `This feature requires ${tier} tier or higher. Upgrade now to access.`;
  const reasonDisabled = !isAuthorizedToManageBilling ? 'Only admins can upgrade' : '';
  const escapeNavCopy = pageBlock ? 'Go Back' : 'Close';

  async function openBillingPortal() {
    const { data } = await createBillingSessionUrl();
    if (data) redirectTo(data.createBillingSessionUrl);
  }

  function handleClickSecondary() {
    setTierGuardModalState({ open: false });
    if (pageBlock) return history.length ? navigate(-1) : navigate(routes.Home);
  }

  return (
    <ConfirmationDialog
      disableAltClose={pageBlock}
      message={copy}
      open={open}
      primaryDisabledReason={reasonDisabled}
      primaryText="Upgrade"
      secondaryText={escapeNavCopy}
      title="Upgrade required"
      variant={ConfirmationDialogVariant.Info}
      onClickPrimary={openBillingPortal}
      onClickSecondary={handleClickSecondary}
      onClose={() => {
        // Don't enable closing of the modal if pageBlock is on.
        if (pageBlock) return;

        setTierGuardModalState({ open: false });
      }}
    />
  );
}

/**
 * Appears when user with insufficient org role attempts an action.
 */
function RoleGuardModal({
  open,
  requiredRole = Apollo.OrgRole.Basic,
}: RoleGuardModalState): JSX.Element {
  const role = displayRole(requiredRole);
  const [requestUpgrade] = Apollo.useRequestUpgradeMutation();
  const dispatchRequestedNotification = useDispatchNotification({
    message: 'Upgrade request sent.',
    variant: 'success',
  });

  async function handleRequestUpgrade() {
    const { data } = await requestUpgrade({
      variables: {
        input: {
          requestedRole: requiredRole,
        },
      },
    });
    if (!data?.requestUpgrade) return;

    dispatchRequestedNotification();
  }

  return (
    <ConfirmationDialog
      message={`This action requires ${role} role or higher. Click "Request Upgrade" to send an upgrade request to an Admin.`}
      open={open}
      primaryText="Request Upgrade"
      secondaryText="Close"
      title="Role upgrade required"
      variant={ConfirmationDialogVariant.Info}
      onClickPrimary={handleRequestUpgrade}
      onClickSecondary={() => setRoleGuardModalState({ open: false })}
      onClose={() => setRoleGuardModalState({ open: false })}
    />
  );
}

interface TierGuardModalState {
  open: boolean;
  pageBlock?: boolean;
  reachedLimit?: boolean;
  requiredTier?: Apollo.SubscriptionTier;
}

interface RoleGuardModalState {
  open: boolean;
  requiredRole?: Apollo.OrgRole;
}

const tierGuardModalOpenVar = makeVar<TierGuardModalState>({
  open: false,
});

const roleGuardModalOpenVar = makeVar<RoleGuardModalState>({
  open: false,
});

function setTierGuardModalState(state: TierGuardModalState): void {
  tierGuardModalOpenVar(state);
}

function setRoleGuardModalState(state: RoleGuardModalState): void {
  roleGuardModalOpenVar(state);
}

function useTierGuardModalState() {
  return useReactiveVar(tierGuardModalOpenVar);
}

function useRoleGuardModalState() {
  return useReactiveVar(roleGuardModalOpenVar);
}

interface UseAccessGuardOptions<TArgs extends unknown[], TReturn> {
  callback: (...args: TArgs) => TReturn;
  minRole?: Apollo.OrgRole;
  minTier?: Apollo.SubscriptionTier;
}

export function useAccessGuard<TArgs extends unknown[], TReturn>({
  callback,
  minRole,
  minTier,
}: UseAccessGuardOptions<TArgs, TReturn>) {
  const { currentUser, loading } = useCurrentUserQuery();

  /** Not finished authenticating */
  if (loading) return doNothing;

  if (minRole && !getIsUserRoleSufficient(currentUser?.role, minRole))
    return () => setRoleGuardModalState({ open: true, requiredRole: minRole });

  if (minTier) {
    const isTierSufficient = getIsUserTierSufficient(minTier, {
      alineSubscription: currentUser?.org.alineSubscription,
      org: currentUser?.org,
    });
    if (!isDev && !isTierSufficient)
      return () => setTierGuardModalState({ open: true, requiredTier: minTier });
  }

  return callback;
}

/**
 * On page component render, puts up `TierGuardModal` if `requiredTier` is not met.
 */
export function useTierPageGuard(requiredTier: Apollo.SubscriptionTier): void {
  const { currentUser, loading } = useCurrentUserQuery();

  useEffect(() => {
    /** Not finished authenticating */
    if (loading) return;

    const isTierSufficient = getIsUserTierSufficient(requiredTier, {
      alineSubscription: currentUser?.org.alineSubscription,
      org: currentUser?.org,
    });
    if (!isDev && !isTierSufficient)
      setTierGuardModalState({ open: true, pageBlock: true, requiredTier });
  }, [requiredTier, currentUser, loading]);
}

/**
 * Use for volume-capped methods.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useTierWorkflowCapGuard<K, T extends (...args: any[]) => K>(
  callback: T
): T | (() => void) {
  const { currentUser, loading } = useCurrentUserSubscription();

  /** Not finished authenticating */
  if (loading || !currentUser) return doNothing;

  /** Already at limit. Throw modal. */
  if (getIsUserOverWorkflowCap(currentUser))
    return () => setTierGuardModalState({ open: true, reachedLimit: true });

  return callback;
}

// === Helpers ===

/**
 * Compares user access vs a required tier level.
 * Takes into account the possibility of org trial (now managed in Org, not BillingInfo).
 */
function getIsUserTierSufficient(
  requiredTier: Apollo.SubscriptionTier,
  context?: {
    alineSubscription?: Pick<Apollo.AlineSubscription, 'tier'> | null;
    org?: Pick<Apollo.Org, 'trialEndAt'> | null;
  }
): boolean {
  const { alineSubscription, org } = context ?? {};
  const trialEndAt = org?.trialEndAt;
  const isInTrial = Boolean(trialEndAt && isAfter(parseISO(trialEndAt), new Date()));
  const tier = isInTrial ? Apollo.SubscriptionTier.Enterprise : alineSubscription?.tier;
  if (!tier) return false;

  return isTierGreaterOrEqual(tier, requiredTier);
}

export interface OrgWithTrialInfo extends Pick<Apollo.Org, 'trialEndAt'> {
  alineSubscription: Pick<Apollo.AlineSubscription, 'tier'> | null;
}

/**
 * Returns true if user has reached the limit of workflows for the month.
 */

export function getIsUserOverWorkflowCap(
  currentUser: Pick<Apollo.User, 'numWorkflowsCreatedThisMonth'> & {
    org: OrgWithTrialInfo | null;
  }
): boolean {
  const isTeamTierOrGreater = getIsUserTierSufficient(Apollo.SubscriptionTier.Team, {
    alineSubscription: currentUser.org?.alineSubscription,
    org: currentUser.org,
  });
  if (isDev || isCI || isTeamTierOrGreater) return false;

  return currentUser.numWorkflowsCreatedThisMonth >= MAX_WORKFLOWS_PER_MONTH;
}

const roleOrder: Apollo.OrgRole[] = [
  Apollo.OrgRole.Viewer,
  Apollo.OrgRole.Basic,
  Apollo.OrgRole.Full,
  Apollo.OrgRole.Admin,
];

function getIsUserRoleSufficient(
  role: Apollo.OrgRole | null | undefined,
  requiredRole: Apollo.OrgRole
) {
  if (!role) return false;
  return roleOrder.indexOf(role) >= roleOrder.indexOf(requiredRole);
}

function displayTier(tier: Apollo.SubscriptionTier): string {
  switch (tier) {
    case Apollo.SubscriptionTier.Team:
      return 'Team';
    case Apollo.SubscriptionTier.Enterprise:
      return 'Enterprise';
  }
}

function displayRole(role: Apollo.OrgRole): string {
  switch (role) {
    case Apollo.OrgRole.Admin:
      return 'Admin';
    case Apollo.OrgRole.Basic:
      return 'Basic';
    case Apollo.OrgRole.Full:
      return 'Full';
    case Apollo.OrgRole.Viewer:
      return 'Viewer';
  }
}

const tierOrder: Apollo.SubscriptionTier[] = [
  Apollo.SubscriptionTier.Team,
  Apollo.SubscriptionTier.Enterprise,
];

function isTierGreaterOrEqual(
  tier1: Apollo.SubscriptionTier,
  tier2: Apollo.SubscriptionTier
): boolean {
  return tierOrder.indexOf(tier1) >= tierOrder.indexOf(tier2);
}
